/**
 * A list of rewards, bundles or triggers.
 *
 * One component for all three. The alternative — three near-identical list
 * screens — is exactly how the predecessors ended up with four copies of
 * their pool filter, three of which did not know about one of the filters
 * (L15). Where the three genuinely differ is the row, and only the row.
 */
import type { ReactNode } from 'react';
import type { Bundle, Reward, Trigger } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Empty, Failed, Link, Spinner } from '../components/primitives.jsx';
import { hrefFor } from '../lib/router.js';
import { bundles, rewards, triggers, type CatalogKind } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useSession } from '../Session.jsx';
import { navigate } from '../lib/router.js';

export function CatalogList(
  { channelId, kind }: { channelId: string; kind: CatalogKind }
): ReactNode {
  const session = useSession();
  const [result, retry] = useAsync(
    (signal) => loaderFor(kind)(channelId, signal),
    [channelId, kind]
  );

  // Hidden, not disabled, when the role cannot author. A button that always
  // fails is worse than no button — and the server refuses it regardless.
  const canCreate = session.channels
    .find((c) => c.id === channelId)?.scopes.includes('rewards:write') ?? false;
  const create = (): void => { navigate(hrefFor([channelId, kind, 'new'])); };

  return (
    <Shell channelId={channelId}>
      {canCreate ? (
        <div className="mb-6 flex justify-end">
          <Button onClick={create}>New {kind.slice(0, -1)}</Button>
        </div>
      ) : null}
      {result.state === 'loading' ? <Spinner label={`Loading ${kind}…`} /> : null}

      {result.state === 'failed' ? (
        // A failed request and an empty list are DIFFERENT, and a console
        // that renders the first as the second tells a streamer their catalog
        // is gone.
        <Failed
          message={`Could not load ${kind}.`}
          detail={result.error.message}
          onRetry={retry}
        />
      ) : null}

      {result.state === 'ready' && result.value.length === 0 ? (
        <Empty
          title={`No ${kind} yet.`}
          hint={HINTS[kind]}
          action={canCreate ? <Button onClick={create}>Create the first one</Button> : undefined}
        />
      ) : null}

      {result.state === 'ready' && result.value.length > 0 ? (
        <ul className="divide-y divide-ink-800 rounded-lg border border-ink-800">
          {result.value.map((entry) => (
            <li key={entry.id}>
              <Link
                href={hrefFor([channelId, kind, entry.id])}
                className="flex items-center gap-4 px-4 py-3 hover:bg-ink-900"
              >
                <Row kind={kind} entry={entry} />
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </Shell>
  );
}

const HINTS: Record<CatalogKind, string> = {
  rewards: 'A reward is a print run over some art. Odds live here, not on the art.',
  bundles: 'A bundle is a loot table. Static delivers its whole pool; dynamic draws from it.',
  triggers: 'A trigger is a rule: something happens on Twitch, and someone gets paid.',
};

type Entry = { id: string; name?: string };

function loaderFor(kind: CatalogKind) {
  return (channelId: string, signal: AbortSignal): Promise<Entry[]> => {
    switch (kind) {
      case 'rewards': return rewards.list(channelId, signal);
      case 'bundles': return bundles.list(channelId, signal);
      case 'triggers': return triggers.list(channelId, signal);
    }
  };
}

function Row({ kind, entry }: { kind: CatalogKind; entry: Entry }): ReactNode {
  return (
    <>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ink-50">{entry.name ?? entry.id}</span>
        <span className="block truncate font-mono text-xs text-ink-700">{entry.id}</span>
      </span>
      {kind === 'rewards' ? <RewardMeta reward={entry as Reward} /> : null}
      {kind === 'bundles' ? <BundleMeta bundle={entry as Bundle} /> : null}
      {kind === 'triggers' ? <TriggerMeta trigger={entry as Trigger} /> : null}
    </>
  );
}

function RewardMeta({ reward }: { reward: Reward }): ReactNode {
  return (
    <span className="flex shrink-0 items-center gap-2">
      {reward.active ? null : <Badge tone="warn">Inactive</Badge>}
      {/* Summed across every counter this reward owns — a print sheet has one
          per (item, tier), so there is no single limit to show. */}
      <Badge tone={reward.stock.remaining === 0 ? 'warn' : 'neutral'}>
        {reward.stock.capacity === null
          ? `${String(reward.stock.minted)} minted · unlimited`
          : `${reward.stock.minted.toLocaleString()} / ${reward.stock.capacity.toLocaleString()}`}
      </Badge>
    </span>
  );
}

function BundleMeta({ bundle }: { bundle: Bundle }): ReactNode {
  return (
    <span className="flex shrink-0 items-center gap-2">
      {bundle.active ? null : <Badge tone="warn">Inactive</Badge>}
      <Badge tone="brand">
        {bundle.slots.reduce((n, s) => n + s.count, 0)} per pack
      </Badge>
      <Badge>
        {bundle.slots.length} {bundle.slots.length === 1 ? 'slot' : 'slots'}
      </Badge>
    </span>
  );
}

function TriggerMeta({ trigger }: { trigger: Trigger }): ReactNode {
  return (
    <span className="flex shrink-0 items-center gap-2">
      {/* A disabled trigger is one that silently never fires — the single
          most useful thing to see from a list. */}
      {trigger.enabled ? null : <Badge tone="warn">Disabled</Badge>}
      {trigger.types.map((type) => <Badge key={type}>{type}</Badge>)}
    </span>
  );
}
